import { useState } from 'react';
import { typography, radius, semantic } from '@/shared/theme/tokens';
import { REC_IMPACT_META, OPEN_LOOP_META, withAlpha } from '../../liveModeMeta';
import { MONO } from './recommendedTheme';
import { FlipNumber } from './FlipNumber';

const TERMINAL = ['acted_on', 'rejected', 'expired'];

/**
 * The recommendation work-queue — the hero of the Recommended screen.
 *
 * AND framework: recs are NOT triaged (no top-pin, no impact ordering). Every
 * actionable rec is shown as equal, the journey is "clear them all". A progress
 * header tracks cleared/total this session; each row surfaces the setpoint move
 * inline (flip numerals) and carries inline Acknowledge / Mark-done actions, plus
 * opens the full drawer (detail + reject-with-comment) on row click.
 */
export function RecQueue({ recs, allRecs, degraded, animate, c, onSelect, onQuickAct }) {
  const total = allRecs.length;
  const cleared = allRecs.filter((r) => TERMINAL.includes(r.status)).length;
  const remaining = recs.length;
  const pct = total ? Math.round((cleared / total) * 100) : 0;

  return (
    <section style={{
      display: 'flex', flexDirection: 'column', minHeight: 0, height: '100%',
      background: c.bg, border: `1px solid ${c.border}`, borderRadius: radius.xl, overflow: 'hidden',
    }}>
      {/* progress header */}
      <div style={{ padding: '13px 16px 12px', borderBottom: `1px solid ${c.border}`, flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12 }}>
          <span style={{
            fontFamily: typography.fontFamily.heading, fontSize: typography.fontSize.lg,
            fontWeight: typography.fontWeight.bold, color: c.textPrimary,
            letterSpacing: typography.letterSpacing.wide, textTransform: 'uppercase',
          }}>
            Recommendations
          </span>
          <span style={{ fontFamily: MONO, fontSize: typography.fontSize.xs, color: c.textSubtle, letterSpacing: typography.letterSpacing.wide, textTransform: 'uppercase', fontVariantNumeric: 'tabular-nums' }}>
            {cleared}/{total} cleared · act on all
          </span>
        </div>
        {/* progress bar */}
        <div style={{ marginTop: 9, height: 6, borderRadius: radius.full, background: c.bgMuted, overflow: 'hidden' }}>
          <div style={{
            width: `${pct}%`, height: '100%', borderRadius: radius.full,
            background: remaining === 0 ? semantic.successAlt : semantic.brandAccent,
            transition: animate ? 'width 0.5s ease' : 'none',
          }} />
        </div>
        <div style={{ marginTop: 7, fontFamily: MONO, fontSize: typography.fontSize.xs, color: remaining ? semantic.warningSolid : semantic.successAlt, letterSpacing: typography.letterSpacing.wide, textTransform: 'uppercase', fontVariantNumeric: 'tabular-nums' }}>
          {degraded ? 'Advisory paused — stale data' : remaining === 0 ? 'All actions cleared' : `${remaining} action${remaining === 1 ? '' : 's'} to clear`}
        </div>
      </div>

      {/* queue */}
      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
        {degraded ? (
          <Empty c={c} icon="sensors_off" color={semantic.infoCyan} title="Advisory paused"
            sub="Live inputs are stale — recommendations are held until the feed recovers." />
        ) : recs.length === 0 ? (
          <Empty c={c} icon="task_alt" color={semantic.successAlt} title="All actions cleared"
            sub="Nothing needs an operator decision right now." />
        ) : (
          recs.map((rec) => (
            <RecRow key={rec.id} rec={rec} animate={animate} c={c} onSelect={onSelect} onQuickAct={onQuickAct} />
          ))
        )}
      </div>
    </section>
  );
}

function RecRow({ rec, animate, c, onSelect, onQuickAct }) {
  const [pending, setPending] = useState(null); // 'accepted' | 'acted_on' | null
  const impact = REC_IMPACT_META[rec.impact] ?? REC_IMPACT_META.low;
  const numerical = rec.action?.type === 'numerical';
  const openLoop = rec.status === 'accepted';

  async function act(e, action) {
    e.stopPropagation();
    if (pending || typeof onQuickAct !== 'function') return;
    setPending(action);
    try { await onQuickAct(rec, action); } finally { setPending(null); }
  }

  return (
    <div
      onClick={() => onSelect?.(rec)}
      onKeyDown={(e) => { if (e.key === 'Enter') onSelect?.(rec); }}
      role="button"
      tabIndex={0}
      title={`Open ${rec.parameterName} recommendation`}
      style={{
        display: 'flex', alignItems: 'center', gap: 14, padding: '14px 16px',
        borderBottom: `1px solid ${c.border}`, cursor: 'pointer',
        borderLeft: `3px solid ${withAlpha(impact.color, '88')}`,
        background: openLoop ? withAlpha(OPEN_LOOP_META.color, '12') : 'transparent',
        animation: animate ? 'rec-in 0.3s ease' : 'none',
        opacity: pending ? 0.6 : 1, transition: 'opacity 0.2s ease',
      }}
    >
      {/* identity */}
      <div style={{ flex: 1.3, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
        <span style={{
          fontFamily: typography.fontFamily.heading, fontSize: typography.fontSize.lg,
          fontWeight: typography.fontWeight.bold, color: c.textPrimary,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {rec.parameterName}
        </span>
        {rec.expectedImpact && (
          <span style={{ fontSize: typography.fontSize.sm, color: c.textMuted, lineHeight: 1.4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {rec.expectedImpact}
          </span>
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <Tag color={impact.color}>{impact.label}</Tag>
          <span style={{ fontFamily: MONO, fontSize: typography.fontSize.xs, color: c.textSubtle, letterSpacing: typography.letterSpacing.wide, textTransform: 'uppercase', fontVariantNumeric: 'tabular-nums' }}>
            Δ in {rec.timeToImpact}
          </span>
          {openLoop && (
            <span style={{
              fontFamily: MONO, fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.bold,
              letterSpacing: typography.letterSpacing.wide, textTransform: 'uppercase', color: OPEN_LOOP_META.color,
              border: `1px solid ${OPEN_LOOP_META.color}`, borderRadius: radius.xs, padding: '1px 6px',
            }}>
              {OPEN_LOOP_META.label}
            </span>
          )}
        </div>
      </div>

      {/* setpoint move */}
      <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {numerical ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', justifyContent: 'center' }}>
            <FlipNumber value={rec.action.currentValue} color={c.textMuted} c={c} size="sm" animate={false} />
            <span className="material-symbols-outlined" style={{ fontSize: 22, color: semantic.brandAccent }}>arrow_right_alt</span>
            <FlipNumber value={rec.action.recommendedValue} color={semantic.brandAccent} c={c} size="md" animate={animate} />
            {rec.unit && <span style={{ fontFamily: MONO, fontSize: typography.fontSize.sm, color: c.textSubtle }}>{rec.unit}</span>}
          </div>
        ) : (
          <span style={{ fontSize: typography.fontSize.sm, color: c.textSecondary, textAlign: 'center', lineHeight: 1.4 }}>
            {rec.action?.instruction}
          </span>
        )}
      </div>

      {/* inline actions */}
      <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 8 }} onClick={(e) => e.stopPropagation()}>
        {!openLoop && (
          <ActionBtn onClick={(e) => act(e, 'accepted')} disabled={!!pending}
            color={semantic.infoCyan} icon="check" label={pending === 'accepted' ? '…' : 'Acknowledge'} />
        )}
        <ActionBtn onClick={(e) => act(e, 'acted_on')} disabled={!!pending}
          color={semantic.successAlt} strong icon="task_alt" label={pending === 'acted_on' ? '…' : 'Mark done'} />
      </div>
    </div>
  );
}

function ActionBtn({ onClick, disabled, color, strong, icon, label }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 12px',
        borderRadius: radius.md, cursor: disabled ? 'default' : 'pointer',
        border: `1px solid ${withAlpha(color, strong ? 'aa' : '66')}`,
        background: withAlpha(color, strong ? '24' : '14'),
        color, fontFamily: typography.fontFamily.body, fontSize: typography.fontSize.sm,
        fontWeight: typography.fontWeight.bold, letterSpacing: typography.letterSpacing.normal,
        opacity: disabled ? 0.6 : 1, whiteSpace: 'nowrap',
      }}
    >
      <span className="material-symbols-outlined" style={{ fontSize: 16 }}>{icon}</span>
      {label}
    </button>
  );
}

function Tag({ color, children }) {
  return (
    <span style={{
      fontFamily: MONO, fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.bold,
      letterSpacing: typography.letterSpacing.wide, textTransform: 'uppercase', color,
      border: `1px solid ${withAlpha(color, '66')}`, background: withAlpha(color, '14'),
      borderRadius: radius.xs, padding: '1px 6px',
    }}>
      {children} impact
    </span>
  );
}

function Empty({ c, icon, color, title, sub }) {
  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '32px 24px', textAlign: 'center' }}>
      <span className="material-symbols-outlined" style={{ fontSize: 40, color }}>{icon}</span>
      <span style={{ fontFamily: typography.fontFamily.heading, fontSize: typography.fontSize.lg, fontWeight: typography.fontWeight.bold, color: c.textPrimary }}>{title}</span>
      <span style={{ fontSize: typography.fontSize.sm, color: c.textMuted, maxWidth: 320, lineHeight: 1.5 }}>{sub}</span>
    </div>
  );
}
